<template>
  <v-col cols="12" sm="6" md="4" lg="3" class="kanban-col-wrapper">
    <v-card flat class="KanbanColumn" ref="card">
      <v-toolbar flat dense class="elevation-0">
        <v-toolbar-title>{{ useI18n ? $t(`labels.${flowState.display}`) : flowState.display }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-badge left color="grey lighten-1" disabled v-if="!!entitiesCount" :content="entitiesCount" />
      </v-toolbar>

      <v-divider class="column-divider mb-2 mx-1" />

      <perfect-scrollbar :style="`height:${colHeight}`" :options="{ suppressScrollX: true }">
        <Container
          @drop="onCardDrop"
          @drag-start="(e) => log('drag start', e)"
          @drag-end="(e) => log('drag end', e)"
          :should-accept-drop="shouldAcceptDrop"
          :get-child-payload="getItemPayload"
          drag-class="card-ghost"
          drop-class="card-ghost-drop"
          class="drop-area"
        >
          <Draggable v-for="item in stateItems" :key="item.entity.refid">
            <div class="draggable-item">
              <slot :item="item" />
            </div>
          </Draggable>
        </Container>
      </perfect-scrollbar>
    </v-card>
  </v-col>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { Container, Draggable } from 'vue-smooth-dnd'
import { find, orderBy } from 'lodash/collection'
import { isEmpty } from 'lodash/lang'
import entityPerimeter from '@argon/evo/entities/perimeters'
import { NAMESPACE as BOARD_NAMESPACE } from '@argon/evo/boards/store'
import { NAMESPACE as REALTIME_NAMESPACE } from '@argon/realtime/store'
import { NAMESPACE } from '../store'

export default {
  name: 'KanbanColumn',
  components: { Container, Draggable },
  perimeters: [entityPerimeter],
  props: {
    orgRef: String,
    boardRef: {
      type: [String, Number],
      required: true
    },
    flowState: { type: Object, required: true },
    useI18n: { type: Boolean, default: false },
    height: Number,
    filterStamp: Number,
    counts: String,
    include: String
  },
  data: () => ({
    sortOn: 'position'
  }),

  computed: {
    ...mapState(BOARD_NAMESPACE, ['board', 'transitions', 'stateAttr']),
    colHeight() {
      let v = this.height - 58
      return `${v}px`
    },
    stateItems() {
      let data = this.$store.getters[`${NAMESPACE}/kanbanBoardItems`](this.flowState.name)
      let enhanced = data.map((x) => ({
        ...x,
        readOnly: this.$entity.isAllowed('read', x.entity) && !this.$entity.isAllowed('update', x.entity)
      }))
      return orderBy(data, ['position'], ['asc'])
    },
    entitiesCount() {
      return this.stateItems.length
    },
    realtimeKey() {
      return this.board
        ? this.$store.getters[`${REALTIME_NAMESPACE}/realtimeKey`]({
            category: `${this.board.entity_type}Boards`,
            identifier: this.board.refname
          })
        : null
    }
  },
  methods: {
    ...mapActions(REALTIME_NAMESPACE, ['addListener', 'removeListener']),
    ...mapActions(NAMESPACE, [
      'listBoardItemsForState',
      'clearKanbanEntities',
      'transit',
      'shift',
      'slideEntity',
      'moveEntity'
    ]),

    fetchData() {
      this.loading = true
      return this.listBoardItemsForState({
        boardRef: this.boardRef,
        flowState: this.flowState.name,
        stateAttr: this.stateAttr,
        counts: this.counts,
        include: this.include
      })
        .catch((error) => {
          if (error.status_code === 404) {
            this.$router.push({ name: 404 })
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    requestSort(sortOn) {
      this.sortOn = sortOn
    },
    shouldAcceptDrop(sourceContainerOptions, payload) {
      if (!this.$entity.isAllowed('update', payload.data.entity)) {
        return false
      }
      if (!isEmpty(payload.data.report.resolutions)) {
        return false
      }
      if (payload.state === this.flowState.name) {
        return true
      }
      return !!find(this.transitions, {
        from_state: payload.state,
        to_state: this.flowState.name
      })
    },
    doMoveBoardItem(transitionObject) {
      this.moveEntity(transitionObject).then((resp) => {
        this.transit({ ...resp, boardRef: this.boardRef })
      })
    },
    doSlideBoardItem(shiftObject) {
      this.slideEntity(shiftObject).then((resp) => {
        this.shift({ ...resp, boardRef: this.boardRef })
      })
    },
    onCardDrop(dropResult) {
      // must handle position and transition
      const { removedIndex, addedIndex, payload } = dropResult
      // Nothing to do
      if (removedIndex === null && addedIndex === null) return false
      if (payload.state === this.flowState.name) {
        // shift
        if (removedIndex === addedIndex || addedIndex == null) return false
        let shiftObj = {
          to_state: this.flowState.name,
          removedIndex,
          addedIndex,
          item: payload.data
        }
        return this.doSlideBoardItem(shiftObj)
      }
      // transition
      let transition = find(this.transitions, {
        from_state: payload.state,
        to_state: this.flowState.name
      })
      if (transition) {
        let transObj = {
          from_state: payload.state,
          to_state: this.flowState.name,
          addedIndex,
          item: payload.data,
          transition
        }
        this.doMoveBoardItem(transObj)
      }
    },
    getItemPayload(index) {
      return {
        state: this.flowState.name,
        data: this.stateItems[index]
      }
    },
    dragStart() {},
    log(...params) {}
  },
  watch: {
    realtimeKey: function (v, oldV) {
      if (oldV) {
        this.fetchData()
      }
    },
    filterStamp: function (v) {
      this.fetchData()
    }
  },
  created() {
    this.fetchData().then(() => {
      this.addListener({
        category: `${this.board.entity_type}States`,
        identifier: this.flowState.name
      })
    })
  },
  destroyed() {
    if (this.board) {
      this.removeListener({ category: `${this.board.entity_type}States`, identifier: this.flowState.name })
    }
    let payload = { id: this.boardRef, flowState: this.flowState.name }
    this.clearKanbanEntities(payload)
  }
}
</script>

<style scoped lang="scss">
.kanban-col-wrapper {
  flex: 0 0 auto;
}

.KanbanColumn {
  background-color: transparent;

  ::v-deep .v-toolbar {
    background-color: transparent;

    .v-toolbar__content {
      padding: 0 5px;
      border: none;
    }
  }
}

.drag-content {
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
}

.drop-area {
  padding: 5px;
  // min-width: 250px;
  min-width: 100%;
  min-height: 100%;
  border-style: dotted;
  border-width: 1px;
  border-color: transparent;
  &:hover {
    border-color: var(--v-grey-lighten2);
  }
}

.draggable-item {
  margin-bottom: 5px;
}

.card-ghost {
  transition: transform 0.18s ease;
  transform: rotateZ(2deg);
}

.card-ghost-drop {
  transition: transform 0.18s ease-in-out;
  transform: rotateZ(0deg);
}

.opacity-ghost {
  transition: all 0.18s ease;
  opacity: 0.8;
  /* transform: rotateZ(5deg); */
  background-color: cornflowerblue;
  box-shadow: 3px 3px 10px 3px rgba(0, 0, 0, 0.3);
}

.opacity-ghost-drop {
  opacity: 1;
  /* transform: rotateZ(0deg); */
  background-color: white;
  box-shadow: 3px 3px 10px 3px rgba(0, 0, 0, 0);
}
</style>

<i18n>
{
  "en": {
    "referenceId": "@:labels.referenceId",
    "naturalPosition": "@:labels.naturalPosition"
  },
  "fr": {
    "referenceId": "@:labels.referenceId",
    "naturalPosition": "@:labels.naturalPosition"
  }
}
</i18n>
